/**
 * **Vipps and BankID as ways in** (0127; Thomas, 2026-10-19: *Sign-in with Vipps and BankID, built against the free
 * test environments only … The personal ID number is verified at sign-in, never typed*).
 *
 * Four pieces: the buttons on the sign-in screen, offered only when the server says the provider is configured; the
 * page the provider sends the person back to, which completes a sign-in or adds the eID to the person signed in; a
 * sign-up for a BankID nobody holds yet, which asks for the address the person will sign in with and finishes once
 * that address's link brings them back (D51 row 8); and the Personal profile's Settings section that lists what each
 * eID verified, adds one and removes one. Nothing here
 * asks for a national identity number: the provider verifies it, the API checks it and keeps only the name and the
 * birth date.
 */
import type { MessageKey } from '@autho/i18n';
import { formatCalendarDate, formatDateTime } from '@autho/i18n';
import { Button } from '@autho/ui';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  useCompleteEidLink,
  useFinishEidSignUp,
  useMeEids,
  useRemoveEid,
  useStartEidLink,
} from '../lib/api.ts';
import { eidIntent, rememberEidIntent, useAuth } from '../lib/auth.tsx';
import { clearEidSignUp, eidSignUpWaiting } from '../lib/eid-sign-up.ts';
import { useOnline } from '../lib/online.ts';
import type { Trouble } from '../lib/trouble.ts';
import { TROUBLE_MESSAGE, signInTrouble } from '../lib/trouble.ts';
import { useT } from '../lib/preferences.tsx';
import { useTenant } from '../lib/tenant.tsx';
import { useLocale } from '../pages/common.tsx';

type Provider = 'vipps' | 'bankid';
const PROVIDERS: readonly Provider[] = ['vipps', 'bankid'];
const NAME: Record<Provider, MessageKey> = { vipps: 'eid.vipps', bankid: 'eid.bankid' };

/** The refusals the API can give, each in words; anything else is said as *could not be completed*. */
const REFUSAL: Record<string, MessageKey> = {
  eid_no_flow: 'eid.refused.noFlow',
  eid_state: 'eid.refused.noFlow',
  eid_cancelled: 'eid.refused.cancelled',
  eid_no_sign_up: 'eid.refused.noSignUp',
  email_unverified: 'eid.refused.emailUnverified',
  eid_incomplete: 'eid.refused.incomplete',
  checksum: 'eid.refused.number',
  malformed: 'eid.refused.number',
  no_such_date: 'eid.refused.number',
  birth_date_mismatch: 'eid.refused.number',
  eid_held_by_another: 'eid.refused.heldByAnother',
  eid_has_another: 'eid.refused.hasAnother',
  not_offered: 'eid.refused.notOffered',
};
const refusal = (code: string): MessageKey => REFUSAL[code] ?? 'eid.refused.other';

/** The provider in a return path, `/eid/{provider}/return`, or null for any other path. */
export function eidReturnOf(pathname: string): Provider | null {
  const match = /^\/eid\/(vipps|bankid)\/return\/?$/.exec(pathname);
  return match === null ? null : (match[1] as Provider);
}

/** What the provider put in the query: a code and the state, or an error. */
function answerOf(search: string) {
  const q = new URLSearchParams(search);
  const pick = (key: string) => q.get(key) ?? undefined;
  return Object.fromEntries(
    Object.entries({ code: pick('code'), state: pick('state'), error: pick('error') }).filter(
      ([, v]) => v !== undefined,
    ),
  ) as { code?: string; state?: string; error?: string };
}

/** The sign-in screen's eID buttons. */
export function EidButtons({ offered }: { offered: readonly Provider[] }) {
  const t = useT();
  const { signInWithEid } = useAuth();
  const shown = PROVIDERS.filter((p) => offered.includes(p));
  if (shown.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-2" data-testid="eid-buttons">
      {shown.map((provider) => (
        <Button key={provider} onClick={() => signInWithEid(provider)}>
          {t('eid.signIn', { provider: t(NAME[provider]) })}
        </Button>
      ))}
      <p className="text-xs text-muted">{t('eid.signIn.test')}</p>
    </div>
  );
}

/** Signed out, back from the provider: hand the answer to the API and take the session it mints. */
export function EidReturnSignIn({ provider }: { provider: Provider }) {
  const t = useT();
  const { completeEidSignIn } = useAuth();
  const [refused, setRefused] = useState<string | null>(null);
  const [signUp, setSignUp] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    // Once: a code is good for one exchange, and a second attempt would only be refused.
    if (started.current) return;
    started.current = true;
    void completeEidSignIn(provider, answerOf(window.location.search)).then((outcome) => {
      if ('signUp' in outcome) setSignUp(outcome.signUp.name);
      else if (outcome.ok) window.history.replaceState(null, '', '/');
      else setRefused(outcome.code);
    });
  }, [completeEidSignIn, provider]);
  if (signUp !== null) return <EidSignUpEmail provider={provider} name={signUp} />;
  return refused === null ? (
    <p role="status" className="text-sm text-muted">
      {t('eid.return.working', { provider: t(NAME[provider]) })}
    </p>
  ) : (
    <div className="flex flex-col gap-3">
      <p role="alert" className="text-sm text-danger" data-testid="eid-refused">
        {t(refusal(refused))}
      </p>
      <a href="/" className="text-sm underline-offset-2 hover:underline">
        {t('eid.return.back')}
      </a>
    </div>
  );
}

/**
 * **A sign-up's email** (D51 row 8): BankID said who the person is, and they give the address they will sign in with.
 * The link to it is the sign-in it always was; this browser remembers that a sign-up waits for it.
 */
function EidSignUpEmail({ provider, name }: { provider: Provider; name: string }) {
  const t = useT();
  const { signUpWithEmail } = useAuth();
  const online = useOnline();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [failed, setFailed] = useState<Trouble | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setFailed(null);
    try {
      const outcome = await signUpWithEmail(email);
      if (outcome.ok) setSent(email);
      else setFailed(signInTrouble(outcome, online));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="eid-sign-up">
      <h2 className="text-section">{t('eid.signUp.title')}</h2>
      <p className="text-sm">{t('eid.signUp.verified', { provider: t(NAME[provider]), name })}</p>
      <p className="text-xs text-muted">
        {t('eid.signUp.explain', { provider: t(NAME[provider]) })}
      </p>
      {sent ? (
        <p role="status" className="text-sm">
          {t('auth.signIn.email.sent', { email: sent })}
        </p>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-2">
          <label htmlFor="eid-sign-up-email" className="text-xs font-medium text-muted">
            {t('eid.signUp.email')}
          </label>
          <input
            id="eid-sign-up-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-8 rounded border border-border bg-surface px-2 text-sm focus-visible:outline-2 focus-visible:outline-accent"
          />
          <Button type="submit" variant="primary" disabled={sending}>
            {t('eid.signUp.submit')}
          </Button>
          {failed !== null && (
            <p role="alert" className="text-sm text-danger" data-testid="signin-trouble">
              {t(TROUBLE_MESSAGE[failed])}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

/**
 * **Finishing a sign-up**, once its email link has signed the person in (D51 row 8): asked once, the waiting mark
 * cleared whatever came of it, and the outcome said until the person closes it.
 */
export function EidSignUpFinish() {
  const t = useT();
  const finish = useFinishEidSignUp();
  const started = useRef(false);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (started.current || !eidSignUpWaiting()) return;
    started.current = true;
    finish.mutate(undefined, { onSettled: clearEidSignUp });
  }, [finish]);
  if (!open || finish.isIdle) return null;
  const code =
    finish.error && typeof finish.error === 'object' && 'code' in finish.error
      ? String((finish.error as { code: unknown }).code)
      : 'other';
  return (
    <div
      className="mx-6 mt-4 flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-border bg-surface p-3 text-sm"
      data-testid="eid-sign-up-finish"
    >
      {finish.isPending && <p role="status">{t('eid.signUp.finishing')}</p>}
      {finish.isSuccess && <p role="status">{t('eid.signUp.done')}</p>}
      {finish.isError && (
        <p role="alert" className="text-danger">
          {t(refusal(code))}
        </p>
      )}
      {!finish.isPending && (
        <Button variant="secondary" onClick={() => setOpen(false)}>
          {t('eid.signUp.close')}
        </Button>
      )}
    </div>
  );
}

/** Signed in, back from the provider to add an eID: from the Personal profile, where it is kept. */
export function EidReturnPage({ provider }: { provider: Provider }) {
  const t = useT();
  const { tenantId } = useTenant();
  const link = useCompleteEidLink(tenantId);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    link.mutate({ provider, ...answerOf(window.location.search) });
  }, [link, provider]);
  const code =
    link.error && typeof link.error === 'object' && 'code' in link.error
      ? String((link.error as { code: unknown }).code)
      : 'other';
  return (
    <div className="flex max-w-lg flex-col gap-3 px-6 py-6">
      {link.isPending && (
        <p role="status" className="text-sm text-muted">
          {t('eid.return.working', { provider: t(NAME[provider]) })}
        </p>
      )}
      {link.isSuccess && (
        <p role="status" className="text-sm" data-testid="eid-linked">
          {t('eid.linked', { provider: t(NAME[provider]) })}
        </p>
      )}
      {link.isError && (
        <p role="alert" className="text-sm text-danger" data-testid="eid-refused">
          {t(refusal(code))}
        </p>
      )}
      <a href="/settings" className="text-sm underline-offset-2 hover:underline">
        {t('eid.return.settings')}
      </a>
    </div>
  );
}

/** The Personal profile's eIDs: what each verified, and adding or removing one. */
export function EidSettings() {
  const t = useT();
  const locale = useLocale();
  const { tenantId } = useTenant();
  const eids = useMeEids(tenantId);
  const start = useStartEidLink();
  const remove = useRemoveEid(tenantId);
  if (!eids.data) return null;
  const { offered, eids: held } = eids.data;
  const addable = PROVIDERS.filter(
    (p) => offered.includes(p) && !held.some((e) => e.provider === p),
  );
  return (
    <div className="flex max-w-lg flex-col gap-3" data-testid="eid-settings">
      <p className="text-xs text-muted">{t('eid.settings.explain')}</p>
      {held.length === 0 ? (
        <p className="text-sm text-muted">{t('eid.settings.none')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {held.map((e) => (
            <li
              key={e.provider}
              className="flex flex-wrap items-baseline gap-2 text-sm"
              data-testid="eid-held"
            >
              <span className="font-medium">{t(NAME[e.provider])}</span>
              <span>{e.verifiedName}</span>
              <span className="text-xs text-muted">
                {t('eid.settings.born', { date: formatCalendarDate(e.birthDate, locale) })}
              </span>
              <span className="text-xs text-muted">
                {t('eid.settings.verified', { at: formatDateTime(e.verifiedAt, locale) })}
              </span>
              <Button
                variant="secondary"
                disabled={remove.isPending}
                onClick={() => remove.mutate(e.provider)}
              >
                {t('eid.settings.remove', { provider: t(NAME[e.provider]) })}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {addable.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {addable.map((provider) => (
            <Button
              key={provider}
              variant="secondary"
              disabled={start.isPending}
              onClick={() =>
                start.mutate(provider, {
                  onSuccess: ({ url }) => {
                    rememberEidIntent('link');
                    window.location.assign(url);
                  },
                })
              }
            >
              {t('eid.settings.add', { provider: t(NAME[provider]) })}
            </Button>
          ))}
        </div>
      )}
      {offered.length === 0 && <p className="text-xs text-muted">{t('eid.settings.off')}</p>}
    </div>
  );
}

export { eidIntent };
